import axios from 'src/axios';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { APICONSTANTS } from 'src/components/Constants';
import {
  PSEUDO_LANGUAGE,
  interpolate,
  isEnglish,
  pseudoLocalise
} from 'src/i18n/language';

// How text reaches the screen in another language (TGCLOUD-1893):
//
//   const t = useAutoTranslation();
//   t('Add relative')
//   t('{{count}} reports waiting to be matched', { count })
//
// The English text IS the key. A key the language's bundle lacks is queued, a
// 50 ms batch posts the misses to POST /translation (Google Translate, cached
// per language in `translations`), and every component holding this hook
// re-renders as each answer arrives. Until then the English shows.
//
// ONLY static UI text goes through t(). A value (a name, a note, a lookup
// value, a server message) goes in `vars`, which are filled in AFTER the
// lookup and never leave the browser. Text sent to t() is sent to Google and
// cached where GET /translation/:lang serves it to anyone.
//
// `npm run i18n:check` fails a PR that renders text without t(); see
// scripts/i18n/check.js.

// The bundle's own lookup, not i18next's t(): that one reads "DOB:" as the
// namespace "DOB" and returns '' for it, and splits a key on its dots.
const lookup = (key, lang) => {
  try {
    return i18n.getResource(lang, 'translation')?.[key];
  } catch {
    // i18next not initialised (unit tests): nothing is cached.
    return undefined;
  }
};

// A batch's answers go into the bundle together, then the language is re-set
// ONCE: that is what re-renders every component holding this hook
// (react-i18next's useTranslation listens for it). Re-setting it per string
// re-rendered them once per string.
const addKeys = (translations, lang) => {
  i18n.addResourceBundle(lang, 'translation', translations);
  i18n.changeLanguage(lang);
};

// `${lang}\u0000${key}` present = queued or in flight, or answered with
// nothing this session; not queued again. Keyed per language, so a string
// requested in one language is still requested in the next one chosen.
const translationCache = {};
const cacheKey = (lang, key) => `${lang}\u0000${key}`;

// Pending batch: lang -> Set of texts waiting to be sent
const pendingBatch = {};
const batchTimers = {};

const flushBatch = async (lang) => {
  const texts = [...(pendingBatch[lang] || new Set())];
  delete pendingBatch[lang];
  delete batchTimers[lang];

  if (texts.length === 0) return;

  try {
    const response = await axios.post(APICONSTANTS.updateTranslation, {
      texts,
      lang
    });

    const translationMap = response.data;
    const arrived = {};
    texts.forEach((text) => {
      const translation = translationMap[text];
      if (translation) {
        arrived[String(text)] = translation;
        // Successfully translated — allow re-queue on next load if needed
        delete translationCache[cacheKey(lang, text)];
      }
      // No translation returned (rate limited etc.) — leave in cache so
      // this string is NOT re-queued endlessly in this session.
      // It will be retried on the next page load once the DB has warmed up.
    });
    if (Object.keys(arrived).length) addKeys(arrived, lang);
  } catch (err) {
    // On network/server error, clear cache after a 30s cooldown so strings
    // can be retried — but not immediately, which would cause a tight loop.
    setTimeout(() => {
      texts.forEach((text) => delete translationCache[cacheKey(lang, text)]);
    }, 30000);
    console.error('Translation error occurred');
  }
};

const queueTranslation = (text, currentLanguage) => {
  if (!pendingBatch[currentLanguage]) pendingBatch[currentLanguage] = new Set();
  pendingBatch[currentLanguage].add(text);

  // Mark as queued so t() won't add it again before the batch fires
  translationCache[cacheKey(currentLanguage, text)] = true;

  // Debounce: collect keys for 50ms then send as one batch request
  if (batchTimers[currentLanguage]) clearTimeout(batchTimers[currentLanguage]);
  batchTimers[currentLanguage] = setTimeout(
    () => flushBatch(currentLanguage),
    50
  );
};

/**
 * `text` in `lang`, with `vars` filled in. English, and anything not yet
 * translated, is the text itself.
 *
 * @param {string} lang
 * @param {string} text English UI text, never data
 * @param {Record<string, string|number>} [vars] values for `{{name}}`
 * @returns {string}
 */
export const translateFor = (lang, text, vars) => {
  if (text === null || text === undefined || text === '') return '';
  const source = String(text);
  if (isEnglish(lang)) return interpolate(source, vars);
  if (lang === PSEUDO_LANGUAGE)
    return interpolate(pseudoLocalise(source), vars);

  // A Mongo field name cannot hold a '.', so the stored key has '_' for it.
  const key = source.replace(/\./g, '_');
  const translated = lookup(key, lang);
  if (!translated) {
    if (!translationCache[cacheKey(lang, key)]) queueTranslation(key, lang);
    return interpolate(source, vars);
  }
  return interpolate(String(translated).replace(/_/g, '.'), vars);
};

/**
 * t() for code outside a component: a column definition built at module
 * scope, a helper with no hook. It translates in the current language but
 * subscribes nothing to the language, so prefer the hook wherever one can be
 * called, and call this during render, never once at import.
 */
export const translate = (text, vars) =>
  translateFor(i18n.language, text, vars);

/** The current language code, re-rendering the component when it changes. */
export const useLanguage = () => {
  const { i18n: instance } = useTranslation();
  return instance?.language ?? i18n.language;
};

// Custom hook for translation
export const useAutoTranslation = () => {
  // Subscribes this component to the language, and to each translation as it
  // arrives (addKey re-sets the language). `changed` is a new function after
  // every such event, which is what refreshes the memo below.
  const { t: changed, i18n: instance } = useTranslation();
  const lang = instance?.language ?? i18n.language;

  return useMemo(
    () => (text, vars) => translateFor(lang, text, vars),
    [lang, changed] // eslint-disable-line react-hooks/exhaustive-deps
  );
};
